import { useId, useState, type FormEvent } from "react";
import { useCreateAlertRule, useProjects } from "../../api/queries";
import type { AlertCondition } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import { CONDITION_META, CONDITION_OPTIONS } from "./conditionMeta";
import "../../components/Form.css";

interface CreateAlertRuleModalProps {
  open: boolean;
  onClose: () => void;
}

/** Client-side hint only (Q-7 honesty cuts both ways: this never blocks a
 * submit the server would accept) — the server is the real enforcement
 * point for whatever URL shape it requires. */
function looksLikeHttpsUrl(value: string): boolean {
  return value.trim() === "" || value.trim().toLowerCase().startsWith("https://");
}

export function CreateAlertRuleModal({ open, onClose }: CreateAlertRuleModalProps) {
  const nameId = useId();
  const conditionId = useId();
  const thresholdId = useId();
  const scopeId = useId();
  const webhookId = useId();
  const secretId = useId();
  const enabledId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateAlertRule();
  // Feeds the "Target" scope select below. Cheap and already fetched by
  // ProjectsListPage in the common navigation path, so this isn't gated on
  // `open` the way a heavier request would be (useProjects has no such
  // option today) — it's the same list AlertsPage's own scope-name lookup
  // uses (see AlertsPage.tsx).
  const { data: projectsPage } = useProjects({ limit: 100 });

  const [name, setName] = useState("");
  const [condition, setCondition] = useState<AlertCondition>("node_offline");
  const [threshold, setThreshold] = useState(300);
  const [scope, setScope] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [signingSecret, setSigningSecret] = useState("");
  const [enabled, setEnabled] = useState(true);

  const meta = CONDITION_META[condition];
  const projects = projectsPage?.items ?? [];

  function reset() {
    setName("");
    setCondition("node_offline");
    setThreshold(300);
    setScope("");
    setWebhookUrl("");
    setSigningSecret("");
    setEnabled(true);
  }

  function handleClose() {
    reset();
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedName = name.trim();
    mutation.mutate(
      {
        name: submittedName,
        condition,
        threshold: meta.thresholdLabel ? threshold : 0,
        scope: scope || undefined,
        webhookUrl: webhookUrl.trim() || undefined,
        enabled,
        // Omitted entirely (not sent as "") when blank — there's nothing to
        // clear on a brand-new rule, matching CreateAlertRuleInput's
        // "supply to set, omit for none" contract (client.ts).
        signingSecret: signingSecret || undefined,
      },
      {
        onSuccess: (created) => {
          pushToast({ title: `Alert rule "${created.name}" created.`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't create "${submittedName}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    // The signing secret has been handed off to the mutation above — there's
    // no reason to keep it in component state a moment longer (S-13).
    setSigningSecret("");
  }

  return (
    <Modal open={open} title="New alert rule" onClose={handleClose} initialFocusId={nameId}>
      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={nameId}>
          <span>Name</span>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={200}
          />
        </label>

        <label className="form-field" htmlFor={conditionId}>
          <span>Fires when…</span>
          <select
            id={conditionId}
            value={condition}
            onChange={(e) => setCondition(e.target.value as AlertCondition)}
          >
            {CONDITION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {meta.thresholdLabel && (
          <label className="form-field" htmlFor={thresholdId}>
            <span>{meta.thresholdLabel}</span>
            <input
              id={thresholdId}
              type="number"
              min={0}
              value={threshold}
              onChange={(e) => setThreshold(Math.max(0, Number(e.target.value) || 0))}
              required
            />
            {meta.thresholdHint && <span className="form-field__hint">{meta.thresholdHint}</span>}
          </label>
        )}

        <label className="form-field" htmlFor={scopeId}>
          <span>Target</span>
          <select id={scopeId} value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="">Whole fleet</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field" htmlFor={webhookId}>
          <span>Webhook URL (optional)</span>
          <input
            id={webhookId}
            type="url"
            autoComplete="off"
            placeholder="https://…"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
          />
          <span className="form-field__hint">
            {looksLikeHttpsUrl(webhookUrl)
              ? "Comebye POSTs a non-secret notification here when this rule fires or resolves."
              : "This should be an https:// URL — Comebye will not deliver to a plaintext endpoint."}
          </span>
        </label>

        <label className="form-field" htmlFor={secretId}>
          <span>Webhook signing secret (optional)</span>
          <input
            id={secretId}
            type="password"
            autoComplete="off"
            value={signingSecret}
            onChange={(e) => setSigningSecret(e.target.value)}
          />
          <span className="form-field__hint">
            Sealed on write. Never shown again — used to HMAC-sign each delivery so your endpoint
            can verify it came from Comebye. Leave blank to send unsigned.
          </span>
        </label>

        <label className="form-field form-field--checkbox" htmlFor={enabledId}>
          <span className="form-field--checkbox__row">
            <input
              id={enabledId}
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>Enabled</span>
          </span>
          <span className="form-field__hint">
            A disabled rule is kept but never evaluated — no delivery, no instances.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !name.trim()}>
            {mutation.isPending ? "Creating…" : "Create rule"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
